import type { ReactNode } from "react";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import { Lock } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatformAny, type StaffUser } from "../../../lib/auth";
import { EmptyState, PageHead, TabRail } from "../../ui";
import { SETTINGS_TAB_PERM } from "./perms";
import { InvoiceSettings } from "./invoicing";
import { IntegrationsSettings } from "./integrations";
import { PhoneSettings } from "./phone";
import { PaymentsSettings } from "./payments";
import { LabelSettings } from "./LabelSettings";
import { SupportSettings } from "./SupportSettings";
import { PortalSettings } from "./PortalSettings";
import { RolesSettings } from "./RolesSettings";
import { BrandingSettings } from "./BrandingSettings";
import { ImportSettings } from "./import";
import { LogsSettings } from "./logs";

/**
 * What a tab is about, drawn as a hairline between the groups. Four words for
 * eleven tabs: eleven equal labels in one row are a list you read end to end,
 * where "the money ones / the desk ones / the outside ones / the people ones"
 * is a row you aim at. It is a grouping mark only - the tabs stay one strip in
 * setup order, and nothing here is a heading a reader has to open.
 */
type TabGroup = "money" | "desk" | "links" | "people";

interface SettingsTab {
  /** URL segment under /settings, and the key into `SETTINGS_TAB_PERM`. */
  path: keyof typeof SETTINGS_TAB_PERM;
  labelKey: string;
  group: TabGroup;
  /** Owns sub-routes and therefore needs the `/*` suffix. */
  nested?: boolean;
  panel: (props: { user: StaffUser }) => ReactNode;
}

/**
 * The tabs, in setup order. Every one of them is gated - see `./perms.ts` for
 * which right and why. The menu and the routes are built from the same filtered
 * list, so a permission that hides a tab removes the route behind it rather
 * than leaving it reachable by typing the URL.
 */
const TABS: SettingsTab[] = [
  { path: "invoicing", labelKey: "settings.tabs.invoicing", group: "money", panel: InvoiceSettings },
  // Second, right behind Invoices, because the two are the same document seen
  // from opposite ends: Invoices says what the paper prints, this says how the
  // reader is allowed to settle it. An operator who has just typed an IBAN is
  // one tab away from the switch that decides whether it is shown at all.
  {
    path: "payments",
    labelKey: "settings.tabs.payments",
    group: "money",
    nested: true,
    panel: PaymentsSettings,
  },
  { path: "appearance", labelKey: "settings.tabs.appearance", group: "desk", panel: BrandingSettings },
  { path: "labels", labelKey: "settings.tabs.labels", group: "desk", panel: LabelSettings },
  // Next to Labels rather than inside Integrations: the response-time promises
  // are the desk's own policy, the same kind of thing as its label vocabulary,
  // and nothing here talks to a third party.
  { path: "support", labelKey: "settings.tabs.support", group: "desk", panel: SupportSettings },
  // Beside Support rather than under Integrations, for the same reason Support
  // is: what a client is shown in their portal is this desk's own policy, and
  // nothing on the panel talks to a third party. It sits after Support because
  // it is the widest-reaching switch in Settings - one save changes every
  // client's portal at once - and that is not the tab a menu should open on.
  { path: "portal", labelKey: "settings.tabs.portal", group: "desk", panel: PortalSettings },
  {
    path: "integrations",
    labelKey: "settings.tabs.integrations",
    group: "links",
    nested: true,
    panel: IntegrationsSettings,
  },
  // The phone sits beside Integrations rather than inside it: nine panels about
  // one desk are a section of their own, and while they were folded in there
  // the Integrations menu read as "the phone, and four other things".
  { path: "phone", labelKey: "settings.tabs.phone", group: "links", nested: true, panel: PhoneSettings },
  { path: "roles", labelKey: "settings.tabs.roles", group: "people", panel: RolesSettings },
  // After Roles, before Import. It belongs beside Roles because both answer
  // questions about PEOPLE rather than about the desk's settings - one says who
  // may do what, the other says what they then did - and it stays ahead of
  // Import for the reason the comment below gives.
  { path: "logs", labelKey: "settings.tabs.logs", group: "people", panel: LogsSettings },
  // Last, and deliberately so: bringing the old CRM across is a one-off piece
  // of setup, not a setting anybody returns to. It is also the only tab here
  // that can delete rows, and the far end of the strip is the right distance
  // from the tab somebody opens every week.
  { path: "import", labelKey: "settings.tabs.import", group: "people", panel: ImportSettings },
];

/**
 * The tab pill. The same shape as `PillTabs`, which is what every other tab row
 * in this CRM wears - but quieter (`.crm-tabs-quiet`, on the rail itself): a
 * toolbar shows three or four filters and can afford a raised surface each,
 * while this row is eleven places and read as eleven floating buttons.
 *
 * These tabs are places, not filter state, so each one stays a real link.
 * `NavLink` sets `aria-current="page"` on the active one, which is the honest
 * role for navigation; `role="tab"` would promise a tabpanel relationship that
 * a routed page does not have.
 */
const tabClass = ({ isActive }: { isActive: boolean }) => cn("inline-flex items-center", isActive && "is-on");

/**
 * The strip, with a hairline wherever the group changes.
 *
 * Built from the tabs this reader can actually see rather than from the full
 * registry, which is what keeps the rule honest: a permission that hides the
 * whole of one group must not leave its divider behind as a rule against the
 * end of the row, or two rules touching where a group used to be.
 */
function railItems(tabs: SettingsTab[], label: (key: string) => string): ReactNode[] {
  const out: ReactNode[] = [];
  tabs.forEach((tab, i) => {
    const prev = tabs[i - 1];
    if (prev && prev.group !== tab.group) {
      out.push(<span key={`div-${tab.path}`} className="crm-tabs-div" aria-hidden />);
    }
    out.push(
      <NavLink key={tab.path} to={`/settings/${tab.path}`} className={tabClass}>
        {label(tab.labelKey)}
      </NavLink>,
    );
  });
  return out;
}

/**
 * Settings - invoicing, appearance, labels, integrations and roles.
 *
 * The section used to sit behind one key, `manage:ariregister`, which was right
 * when the only thing in here was the Business Register mandate. It has not
 * been right since: a mailbox owner could not reach the label vocabulary their
 * own desk writes, and the register right was the only way in. `/settings` now
 * opens for anyone holding at least one tab's right (`SETTINGS_PERMS` in
 * `crm/nav.ts`), and each tab keeps its own gate.
 */
export function SettingsSection({ user }: { user: StaffUser }) {
  const t = useT();
  const tabs = TABS.filter((tab) => userCanPlatformAny(user, SETTINGS_TAB_PERM[tab.path]));

  // The landing tab is the first one this staff member actually has. It used to
  // be invoicing unconditionally, which was harmless only while everybody who
  // could open Settings could also see that tab; now it would drop a call
  // operator on a redirect loop between a route they cannot reach and here.
  const home = tabs[0] ? `/settings/${tabs[0].path}` : null;

  return (
    <>
      <PageHead title={t("settings.title")} sub={t("settings.subtitle")} />

      {home === null ? (
        // Unreachable through the sidebar - `SETTINGS_PERMS` is the union of the
        // tab gates, so an empty list means no route was mounted either. It is
        // here because a permission removed while the page is open must not
        // render a blank card with no explanation.
        <div className="crm-card">
          <EmptyState icon={Lock} title={t("settings.noAccessTitle")} body={t("settings.noAccess")} />
        </div>
      ) : (
        <>
          {/* One line that scrolls, not two that wrap. Twelve tabs never fitted
              on a laptop, and the pair that fell off read as a second group of
              settings rather than as the end of the first. `TabRail` keeps the
              open one in view, which matters more here than anywhere else: this
              row is opened straight from a URL.

              The quiet pill is what put the row back on one line for most
              readers - it is the same eleven words at 14px of side padding
              instead of 22px - so the rail is now the safety net it was meant to
              be rather than the everyday state. */}
          <TabRail as="nav" className="mb-5" tabsClassName="crm-tabs-quiet" label={t("settings.title")}>
            {railItems(tabs, t)}
          </TabRail>

          <Routes>
            <Route index element={<Navigate to={home} replace />} />
            {tabs.map(({ path, nested, panel: Panel }) => (
              <Route key={path} path={nested ? `${path}/*` : path} element={<Panel user={user} />} />
            ))}
            {/* The Stripe panel used to answer under Integrations. Its redirect
                is HERE rather than inside that subsection, which is where the
                nine phone ones live, because the subsection is only mounted for
                somebody holding an Integrations right - and card payments was
                the entry that put `manage:crm.sales` into that list. A sales
                operator opening the bookmark they saved last month would
                otherwise land on Invoices with no explanation.

                A static path outranks the `integrations/*` route above it in
                React Router's matcher, so this wins for a reader who has both. */}
            <Route
              path="integrations/card-payments"
              element={<Navigate to="/settings/payments/card-payments" replace />}
            />
            <Route path="*" element={<Navigate to={home} replace />} />
          </Routes>
        </>
      )}
    </>
  );
}
